import styled from 'styled-components';

type PreviewProcessingOverlayProps = {
  title?: string;
  body?: string;
  /** Smaller type for mini players (Transcript & Speakers, Video Clips). */
  compact?: boolean;
  'data-testid'?: string;
};

/**
 * PLAT-11224 (audio-first): the shared processing veil for every video player
 * surface while the preview rendition is pending — the Creator Studio canvas,
 * the Content Studio Video Clips player, and the Transcript & Speakers mini
 * player all render this same component so the state reads identically
 * everywhere. The host container must be `position: relative`. Pointer events
 * pass through, so anything interactive underneath stays usable.
 */
export default function PreviewProcessingOverlay({
  title = 'Preview Processing',
  body = 'The video is still processing — it appears here as soon as it’s ready.',
  compact = false,
  'data-testid': dataTestId,
}: PreviewProcessingOverlayProps) {
  return (
    <Veil data-testid={dataTestId}>
      <VeilTitle $compact={compact}>{title}</VeilTitle>
      <VeilText $compact={compact}>{body}</VeilText>
    </Veil>
  );
}

const Veil = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  text-align: center;
  /* Veil the visuals only — playback controls and the rest of the surface
     stay interactive underneath. */
  pointer-events: none;
  background-color: color-mix(in srgb, var(--background) 55%, transparent);
`;

const VeilTitle = styled.h2<{ $compact?: boolean }>`
  margin: 0;
  font-size: ${props => (props.$compact ? 'var(--text-sm)' : '20px')};
  font-weight: var(--font-weight-semibold);
  color: var(--foreground);
`;

const VeilText = styled.p<{ $compact?: boolean }>`
  margin: 0;
  max-width: 480px;
  font-size: ${props => (props.$compact ? 'var(--text-xs)' : 'var(--text-sm)')};
  color: var(--text-muted);
`;
